import React from "react";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";
import clsx from "clsx";
import { Column } from "@webbio/components";

import {
  DescriptionRunEventDTO,
  DescriptionRunEventDTOActionEnum,
  DescriptionRunEventDTOSkipReasonEnum
} from "@app/api/generated";
import { Table } from "@app/components/table/table";
import { formatConfidence } from "@app/util/format-confidence";
import { ROUTES } from "@app/constants/routes";

import styles from "@app/styles/dashboard/events-table.module.scss";

import { EVENT_ACTION_LABEL_KEY, SKIP_REASON_LABEL_KEY } from "../event-actions";

interface IEventsTableProps {
  events: DescriptionRunEventDTO[];
  isLoading: boolean;
}

/** Per-outcome badge accent (base badge = grey). */
const ACTION_BADGE_CLASS: Partial<Record<DescriptionRunEventDTOActionEnum, string>> = {
  [DescriptionRunEventDTOActionEnum.WRITTEN]: styles.badgeBlue,
  [DescriptionRunEventDTOActionEnum.SKIPPED]: styles.badgeYellow,
  [DescriptionRunEventDTOActionEnum.ERROR]: styles.badgeRed
};

const EventsTable = ({ events, isLoading }: IEventsTableProps): JSX.Element => {
  const intl = useIntl();

  return (
    <Table dataSource={events} isLoading={isLoading} noHover>
      <Column
        field="action"
        title={intl.formatMessage({ id: "productDescription.events.table.action" })}
        maxWidth={150}
        width={150}
        cell={(action: DescriptionRunEventDTOActionEnum) => (
          <span className={clsx(styles.badge, ACTION_BADGE_CLASS[action])}>
            {intl.formatMessage({ id: EVENT_ACTION_LABEL_KEY[action] })}
          </span>
        )}
      />
      <Column
        field="skipReason"
        title={intl.formatMessage({ id: "productDescription.events.table.reason" })}
        cell={(skipReason: DescriptionRunEventDTOSkipReasonEnum | null) => (
          <>{skipReason ? intl.formatMessage({ id: SKIP_REASON_LABEL_KEY[skipReason] }) : "—"}</>
        )}
        minWidth={130}
      />
      <Column
        field="productId"
        title={intl.formatMessage({ id: "productDescription.events.table.productId" })}
        maxWidth={64}
        width={64}
        cell={(productId: number | null) =>
          productId != null ? (
            <Link to={`${ROUTES.PRODUCTS}/${productId}`} className={styles.productLink}>
              {productId}
            </Link>
          ) : (
            <>—</>
          )
        }
      />
      <Column
        field="ean"
        title={intl.formatMessage({ id: "productDescription.events.table.ean" })}
        cell={(ean: string | null) => (
          <span className="truncate-table" title={ean ?? "-"}>
            {ean ?? "—"}
          </span>
        )}
        minWidth={120}
      />
      <Column
        field="confidence"
        title={intl.formatMessage({ id: "productDescription.events.table.confidence" })}
        maxWidth={100}
        width={100}
        cell={(confidence: number | null) => <>{confidence != null ? formatConfidence(intl, confidence) : "—"}</>}
      />
      <Column
        field="grounded"
        title={intl.formatMessage({ id: "productDescription.events.table.grounded" })}
        maxWidth={90}
        width={90}
        cell={(grounded: boolean) => (
          <>{intl.formatMessage({ id: grounded ? "productDescription.events.yes" : "productDescription.events.no" })}</>
        )}
      />
      <Column
        field="message"
        title={intl.formatMessage({ id: "productDescription.events.table.message" })}
        cell={(message: string | null) => (
          <span className="truncate-table" title={message ?? "-"}>
            {message ?? "—"}
          </span>
        )}
      />
    </Table>
  );
};

export { EventsTable };
